export {
  reportAppError,
  reportAppEvent,
  reportStorageFallback,
  reportProtoCtxInvalid,
  reportWhopProtoCtxMissing,
};

// Guarded reporting to the first-party error channel (window.Sentry ships via
// the SSR core-support snippet; late-bound guard — never a hard dependency,
// lesson from probe v3). Every report carries tags so retries are legible in
// Sentry: `app_phase` (init/config/save) and `app_attempt` (1-based).
//
// Caveat baked into the design: attempt-failure reports fire while the network
// is typically DEAD (that is why the attempt failed), so many of them will not
// reach ingest — they are best-effort. The reliable signal is the RECOVERY
// event (`*_recovered`, sent when a later attempt succeeds, network alive) and
// the exhaustion event (sent on the next window of connectivity, if any).

type Tags = Record<string, string | number>;

interface SentryLike {
  captureException?: (e: unknown, ctx?: { tags?: Tags }) => void;
  captureMessage?: (m: string, ctx?: { level?: string; tags?: Tags }) => void;
}

function sentry(): SentryLike | undefined {
  return (window as unknown as { Sentry?: SentryLike }).Sentry;
}

function reportAppError(error: unknown, tags: Tags): void {
  try {
    sentry()?.captureException?.(
      error instanceof Error ? error : new Error(String(error)),
      { tags },
    );
  } catch (e) {
    // never break the caller
  }
}

function reportAppEvent(name: string, tags: Tags): void {
  // Nivel "info", no "warning": todo lo que pasa por aquí es telemetría de
  // rescate/medición ("se rompió X pero lo salvamos haciendo Y") —
  // storage_fallback, *_recovered. Los fallos reales van por reportAppError.
  try {
    sentry()?.captureMessage?.(name, { level: "info", tags });
  } catch (e) {
    // never break the caller
  }
}

// Mide la purga de localStorage en mitad de la sesión (WKWebView/ITP,
// hallazgo 31/7/2026): cuando un getter no encuentra su clave y cae a la
// copia en memoria o recalcula, se reporta UNA vez por página y área para
// cuantificar el fenómeno sin fundir la cuota. app_source: memory|recomputed.
const reportedFallbacks = new Set<string>();

function reportStorageFallback(area: string, source: string): void {
  const key = `${area}:${source}`;
  if (reportedFallbacks.has(key)) return;
  // El SDK (support.js) carga async: si aún no está, NO marcar el dedupe —
  // los fallbacks del init se tragaban en silencio y quedaban deduplicados
  // para siempre en esa página (infra-contaba el fenómeno; visto el 3/8: solo
  // llegaba app_phase=tracking y nunca user). Sin Sentry, el próximo disparo
  // de la misma área lo reintenta.
  try {
    if (!sentry()?.captureMessage) return;
  } catch (e) {
    return;
  }
  reportedFallbacks.add(key);
  reportAppEvent("storage_fallback", { app_phase: area, app_source: source });
}

// Mide los `protoctx` que LLEGAN pero no se pueden parsear (rescate de
// atribución, 4/8/2026). Es señal diagnóstica, no un error de la página: si
// Google los TRUNCA o mutila veremos no_separator/missing_medium, mientras que
// "no llegó ninguno" no genera evento — distinguir ambos casos es lo que
// decide si el siguiente paso es inferir el canal por click-id.
//
// Una vez por página y motivo. A diferencia de reportStorageFallback, que se
// llama desde sitios que se repiten durante toda la vida de la página y por
// eso puede limitarse a no marcar el dedupe cuando falta el SDK, este aviso
// tiene UN solo call site —el constructor de Tracking— que corre una vez por
// carga: no hay "próximo disparo" que lo reintente. Y justo ahí el SDK suele
// NO estar todavía (window.Sentry solo llega por el support.js async del
// snippet SSR — el Sentry propio del loader se eliminó el 4/8/2026), con
// lo que sin reintento el contador no se emitiría casi nunca. De ahí la
// escalera de reintentos.
const reportedTrackingIssues = new Set<string>();
const PROTOCTX_RETRY_DELAYS = [1000, 5000, 15000];

function reportProtoCtxInvalid(reason: string): void {
  attemptTrackingReport("protoctx_invalid", reason, 0);
}

// Tráfico de Whop Ads (`utm_whop=true`) que llega SIN un protoctx utilizable
// (reason `absent` o el motivo del parser). Whop secuestra utm_source,
// utm_medium y utm_content, así que sin el `protoctx` estático que el media
// buyer pone en cada anuncio de Whop el lead entra sin atribuir en el CRM
// (degradación aceptada con producto). Este aviso es el que dice qué anuncios
// se montaron sin él. Mismo call site único en el constructor de Tracking
// que protoctx_invalid, así que comparte su escalera de reintentos.
function reportWhopProtoCtxMissing(reason: string): void {
  attemptTrackingReport("whop_protoctx_missing", reason, 0);
}

// Una vez por página, evento y motivo
function attemptTrackingReport(
  event: string,
  reason: string,
  retry: number,
): void {
  const key = event + ":" + reason;
  if (reportedTrackingIssues.has(key)) return;

  let available = false;
  try {
    available = !!sentry()?.captureMessage;
  } catch {
    return;
  }

  if (!available) {
    if (retry < PROTOCTX_RETRY_DELAYS.length) {
      setTimeout(
        () => attemptTrackingReport(event, reason, retry + 1),
        PROTOCTX_RETRY_DELAYS[retry],
      );
    }
    return;
  }

  reportedTrackingIssues.add(key);
  reportAppEvent(event, {
    app_phase: "tracking",
    app_reason: reason,
  });
}
